import { apiClient } from '@/lib/apiClient';
import type {
  ApiResponse,
  Integration,
  SourceConfig,
  SourceConfigCreate,
  SourceConfigUpdate
} from 'dico-types';

export interface SlackChannel {
  id: string;
  name: string;
  isPrivate: boolean;
  isArchived: boolean;
  numMembers?: number;
}

export interface GithubRepo {
  id: number;
  fullName: string;
  private: boolean;
  description: string | null;
}

export interface DiscordChannel {
  id: string;
  name: string;
  /** 0 = text, 5 = announcement, 15 = forum */
  type: number;
}

const base = (workspaceId: string) => `/api/workspaces/${workspaceId}/integration`;

const listAccounts = (workspaceId: string, signal?: AbortSignal) =>
  apiClient.get<ApiResponse<Integration[]>>(base(workspaceId), { signal });

const removeAccount = (workspaceId: string, accountId: string, signal?: AbortSignal) =>
  apiClient.delete<ApiResponse>(`${base(workspaceId)}/${accountId}`, { signal });

const listSources = (workspaceId: string, accountId: string, signal?: AbortSignal) =>
  apiClient.get<ApiResponse<SourceConfig[]>>(`${base(workspaceId)}/${accountId}/sources`, {
    signal
  });

const addSource = (
  workspaceId: string,
  accountId: string,
  data: SourceConfigCreate,
  signal?: AbortSignal
) =>
  apiClient.post<ApiResponse<SourceConfig>>(`${base(workspaceId)}/${accountId}/sources`, data, {
    signal
  });

const updateSource = (
  workspaceId: string,
  accountId: string,
  sourceId: string,
  data: SourceConfigUpdate,
  signal?: AbortSignal
) =>
  apiClient.patch<ApiResponse<SourceConfig>>(
    `${base(workspaceId)}/${accountId}/sources/${sourceId}`,
    data,
    { signal }
  );

const removeSource = (
  workspaceId: string,
  accountId: string,
  sourceId: string,
  signal?: AbortSignal
) =>
  apiClient.delete<ApiResponse>(`${base(workspaceId)}/${accountId}/sources/${sourceId}`, {
    signal
  });

const listSlackChannels = (workspaceId: string, accountId: string, signal?: AbortSignal) =>
  apiClient.get<ApiResponse<SlackChannel[]>>(`${base(workspaceId)}/${accountId}/slack/channels`, {
    signal
  });

const listGithubRepos = (workspaceId: string, accountId: string, signal?: AbortSignal) =>
  apiClient.get<ApiResponse<GithubRepo[]>>(`${base(workspaceId)}/${accountId}/github/repos`, {
    signal
  });

const listDiscordChannels = (workspaceId: string, accountId: string, signal?: AbortSignal) =>
  apiClient.get<ApiResponse<DiscordChannel[]>>(
    `${base(workspaceId)}/${accountId}/discord/channels`,
    { signal }
  );

const syncSource = (
  workspaceId: string,
  accountId: string,
  sourceId: string,
  options?: { daysBack?: number },
  signal?: AbortSignal
) =>
  apiClient.post<ApiResponse<{ messagesSynced: number; ticketsCreated: number }>>(
    `${base(workspaceId)}/${accountId}/sources/${sourceId}/sync`,
    { daysBack: options?.daysBack ?? 30 },
    { signal }
  );

const initKBForSource = (
  workspaceId: string,
  accountId: string,
  sourceId: string,
  signal?: AbortSignal
) =>
  apiClient.post<ApiResponse<{ snapshotId: string; status: string }>>(
    `${base(workspaceId)}/${accountId}/sources/${sourceId}/kb-init`,
    {},
    { signal }
  );

export const integrationApi = {
  listAccounts,
  removeAccount,
  listSources,
  addSource,
  updateSource,
  removeSource,
  listSlackChannels,
  listGithubRepos,
  listDiscordChannels,
  syncSource,
  initKBForSource
};

export {
  listAccounts,
  removeAccount,
  listSources,
  addSource,
  updateSource,
  removeSource,
  listSlackChannels,
  listGithubRepos,
  listDiscordChannels,
  syncSource,
  initKBForSource
};
